"use client";

import { useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { FC, ReactElement, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import type { PlanType } from "../../const/planConst";
import { FAILED_PAYMENT_WALL_ERROR_CODE } from "../../helpers/failedPaymentWall";
import { getPaymentRequiredDialog } from "../../helpers/pricingLimitDialog";
import { defaultAutoPublishAccountIds, type AutoPublishChange, type AutoPublishState } from "../../hooks/useAutoPublish";
import { useConnectAccountPoll } from "../../hooks/useConnectAccountPoll";
import { useProfile } from "../../hooks/useProfile";
import { useScheduleDefaults } from "../../hooks/useScheduleDefaults";
import type { SocialProviderAccount } from "../../hooks/useSocialProviderAccounts";
import ConfirmAction from "../ConfirmAction";
import ConnectAccountsDialog from "../ConnectAccountsDialog";
import { ScheduleCheckoutProvider } from "../ScheduleCheckoutProvider";
import { fetchPublicationCreationPolicy, type PaymentRequired } from "../publicationFlow/schedulingBatch";
import { useStackedModal } from "../StackedModal";
import { AUTO_POST } from "./autoPostName";
import { useSession } from "../SupabaseProvider";

const NOT_AUTO_PUBLISHED = ["linkedin"];
const POLICY_FRESH_MS = 5 * 60 * 1000;

/** Checkout in a tab of its own keeps the submit form underneath, as Schedule's has since #235
 *  (no measurable cost on desktop: 8 of 27 paid within 2 h, vs 17 of 62 before). Touch screens keep
 *  the same-tab redirect: there the scheduler's new tab converted 0 of 17 (vs 7 of 40), 2026-10-28. */
const opensCheckoutInNewTab = () =>
  typeof window !== "undefined" && !window.matchMedia?.("(pointer: coarse)").matches;

/** Asked before turning off a project with posts to go. */
export const AutoPublishTurnOffConfirm: FC<{ upcoming: number; onConfirm: () => void; onCancel: () => void }> = ({
  upcoming,
  onConfirm,
  onCancel,
}) => (
  <ConfirmAction
    title={`Turn off ${AUTO_POST}?`}
    description={`This project's ${upcoming} upcoming ${upcoming === 1 ? "post" : "posts"} from ${AUTO_POST} will be removed. Manually scheduled and published posts stay.`}
    confirmLabel="Turn off"
    onConfirm={onConfirm}
    onCancel={onCancel}
  />
);

/**
 * The switch, for the submit form and the results header alike. On first
 * gets what posting needs, each as its dialog: an account to post to, then a
 * plan (the paywall), Schedule's order since experiment 505483: connecting is
 * free, and the price comes once there is somewhere to post. It lands with
 * accounts picked. Off with posts to go asks first. The dialogs open in a
 * layer of their own, so the submit form and its upload stay underneath. The
 * plan is asked when the pointer or focus reaches the switch (`prefetch`), not
 * on page views: the same moment the policy's analytics (failed-payment wall,
 * watermark exposure) always saw. `checking` while it is still out. Render
 * `layer` once, and give the panel `shownState`: off, it carries the accounts
 * turning on would pick.
 */
export function useAutoPublishSwitch({ surface, state, accounts, apply, onDialog, onReturn, open = false }: {
  surface: "submit" | "results" | "calendar";
  state: AutoPublishState | null;
  /** Where it can post (autoPublishAccounts). */
  accounts: SocialProviderAccount[];
  /** Results and calendar save at once (useAutoPublish's save.mutate); the submit form stages. */
  apply: (change: AutoPublishChange, options?: { onSuccess?: () => void }) => void;
  /** A dialog is opening: the popover closes. */
  onDialog: () => void;
  /** The panel's Connect account got an account: the popover opens again, the switch now in it. */
  onReturn?: () => void;
  /** The panel is showing, for the "Auto Publish Opened" event. */
  open?: boolean;
}) {
  const posthog = usePostHog();
  const session = useSession();
  const profile = useProfile().data ?? null;
  const scheduleAccountIds = useScheduleDefaults().defaults?.accountIds ?? [];
  const { modal, layer } = useStackedModal();
  const { connect } = useConnectAccountPoll();
  // Where turning on stands: asking the plan, at the paywall, connecting.
  const [turningOn, setTurningOn] = useState<"checking" | "paywall" | "connect" | null>(null);
  const queryClient = useQueryClient();
  const requestedEnableChange = useRef<AutoPublishChange | null>(null);
  // A fresh checkout provider for each paywall, as BulkCreatePublicationForm does.
  const checkoutSequence = useRef(0);
  const cancelTurningOn = () => {
    requestedEnableChange.current = null;
    setTurningOn(null);
  };
  // The plan in the key: an upgrade asks again. Only answers are kept: a
  // check that fails throws, so it is never read as "may post".
  const policy = {
    queryKey: ["publication-creation-policy", session?.user.id, profile?.plan, profile?.subscriptionStatus],
    queryFn: () => fetchPublicationCreationPolicy(session?.access_token ?? ""),
    retry: false,
  };
  const prefetch = () => {
    if (state && !state.enabled && session?.access_token && !turningOn)
      void queryClient.prefetchQuery({ ...policy, staleTime: POLICY_FRESH_MS });
  };

  const openDialog = (content: ReactElement, className: string) => {
    onDialog();
    modal.open(content, { className });
  };
  const showConnect = (from: "panel" | "switch") => {
    posthog?.capture("Auto Publish Connect Opened", { surface, from, has_accounts: accounts.length > 0 });
    openDialog(
      <ConnectAccountsDialog
        subtitle={`${AUTO_POST} schedules your shorts to TikTok, YouTube, Instagram or Facebook.`}
        hideProviders={NOT_AUTO_PUBLISHED}
      />,
      "max-w-sm",
    );
  };
  // The panel's Connect account (and Manage accounts). Without an account to
  // post to yet, the dialog closes once one connects and the popover comes
  // back with the switch: connecting never turns Auto post on by itself.
  const [returnOnConnect, setReturnOnConnect] = useState(false);
  const openConnect = () => {
    setReturnOnConnect(accounts.length === 0);
    showConnect("panel");
  };
  // Keep polling mounted when the accounts menu and its parent popover close.
  // Reconnecting preserves the project's selection and never runs the enable flow.
  const reconnectAccount = (account: SocialProviderAccount) => {
    onDialog();
    connect(account.provider, account.id);
  };

  // Without a picked account it can post to, turning on lands on the defaults.
  const picked = Boolean(state?.accountIds.some((id) => accounts.some((account) => account.id === id)));
  const fallbackAccountIds = () => defaultAutoPublishAccountIds(accounts, state?.accountIds ?? [], scheduleAccountIds);
  // Off, the panel previews the plan the switch would make, so it shows those same defaults.
  const shownState = state && !state.enabled && !picked ? { ...state, accountIds: fallbackAccountIds() } : state;

  const turnOn = () => {
    const requested = requestedEnableChange.current ?? { enabled: true };
    const change = {
      ...requested,
      ...(!picked && requested.accountIds === undefined && { accountIds: fallbackAccountIds() }),
    };
    cancelTurningOn();
    // The click is "Auto Publish Toggled"; this is the switch actually landing
    // on: once saved, or staged until Generate on the submit form.
    const landed = () => posthog?.capture("Auto Publish Turned On", {
      surface, accounts: (change.accountIds ?? state?.accountIds ?? []).length, staged: surface === "submit",
    });
    if (surface === "submit") {
      landed();
      apply({ ...change, enabled: true });
    } else apply({ ...change, enabled: true }, { onSuccess: landed });
  };
  // after: the paywall just closed, so a plan still missing means "not now";
  // or an account just connected, with the connect dialog still up.
  const continueTurningOn = async (after?: "paywall" | "connected") => {
    if (accounts.length === 0) {
      showConnect("switch");
      return setTurningOn("connect");
    }
    // Only a fresh "may post" skips asking: a paywall answer is asked again,
    // as the payment may have been fixed since (in another tab, by email).
    const known = queryClient.getQueryState<PaymentRequired | null>(policy.queryKey);
    let paymentRequired: PaymentRequired | null = null;
    if (after === "paywall" || known?.status !== "success" || known.data !== null || Date.now() - known.dataUpdatedAt > POLICY_FRESH_MS) {
      setTurningOn("checking");
      try {
        paymentRequired = await queryClient.fetchQuery({ ...policy, staleTime: 0 });
      } catch {
        toast.error("Couldn't check your plan. Try again.");
        if (modal.isOpen) modal.close();
        return cancelTurningOn();
      }
    }
    if (paymentRequired) {
      if (after === "paywall") return cancelTurningOn();
      const dialog = getPaymentRequiredDialog({
        ...paymentRequired,
        requiredPlan: paymentRequired.requiredPlan as PlanType | undefined,
        profile,
        unpaidVariant: "auto_post",
        action: "publish",
      });
      // A failed renewal gets FailedPaymentWall, which counts itself, as in
      // BulkCreatePublicationForm.
      if (paymentRequired.errorCode !== FAILED_PAYMENT_WALL_ERROR_CODE)
        posthog?.capture("Upgrade Plan Dialog Showed", {
          source: "auto_post",
          surface,
          reason: paymentRequired.errorCode,
          required_plan: paymentRequired.requiredPlan,
        });
      // Replaces the connect dialog when it is still up.
      openDialog(opensCheckoutInNewTab()
        ? <ScheduleCheckoutProvider key={++checkoutSequence.current} note={null}>{dialog.content}</ScheduleCheckoutProvider>
        : dialog.content, dialog.className ?? "max-w-6xl");
      return setTurningOn("paywall");
    }
    if (modal.isOpen) modal.close();
    turnOn();
  };
  // The paywall closed, upgraded or not: ask the plan once more.
  useEffect(() => {
    if (turningOn === "paywall" && !modal.isOpen) void continueTurningOn("paywall");
  }, [turningOn, modal.isOpen, continueTurningOn]);
  // Connecting: once an account it can post to connects, on to the plan;
  // closed without one, it stays off.
  useEffect(() => {
    if (turningOn !== "connect") return;
    if (accounts.length > 0) void continueTurningOn("connected");
    else if (!modal.isOpen) cancelTurningOn();
  }, [turningOn, accounts.length, modal, continueTurningOn]);

  const reopenedAfterConnect = useRef(false);
  useEffect(() => {
    if (!returnOnConnect) return;
    if (accounts.length > 0) {
      setReturnOnConnect(false);
      modal.close();
      reopenedAfterConnect.current = true;
      onReturn?.();
    } else if (!modal.isOpen) setReturnOnConnect(false);
  }, [returnOnConnect, accounts.length, modal, onReturn]);

  // Opens, not views: the experiment's only read on who sees the panel. Sent
  // once per opening, when its settings are known (the calendar loads them after).
  const counted = useRef(false);
  const openEvent = useRef({ surface, on: state?.enabled ?? false, has_accounts: accounts.length > 0 });
  openEvent.current = { surface, on: state?.enabled ?? false, has_accounts: accounts.length > 0 };
  const known = state !== null;
  useEffect(() => {
    if (!open) {
      counted.current = false;
      return;
    }
    if (counted.current || !known) return;
    counted.current = true;
    posthog?.capture("Auto Publish Opened", { ...openEvent.current, after_connect: reopenedAfterConnect.current });
    reopenedAfterConnect.current = false;
  }, [open, known, posthog]);

  const onChange = (change: AutoPublishChange) => {
    if (change.enabled !== undefined)
      posthog?.capture("Auto Publish Toggled", { surface, on: change.enabled, has_accounts: accounts.length > 0 });
    if (change.enabled === true) {
      if (!turningOn && requestedEnableChange.current === null) {
        requestedEnableChange.current = change;
        void continueTurningOn();
      }
    } else if (change.enabled === false && state && state.upcoming > 0) {
      openDialog(
        <AutoPublishTurnOffConfirm
          upcoming={state.upcoming}
          onConfirm={() => {
            modal.close();
            apply({ enabled: false });
          }}
          onCancel={modal.close}
        />,
        "max-w-md",
      );
    } else apply(change);
  };

  return { onChange, openConnect, reconnectAccount, layer, prefetch, shownState, checking: turningOn === "checking", isBusy: turningOn !== null || modal.isOpen };
}
